// @ts-nocheck
import {useInfiniteQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {useState} from "react";
import {
  addGameFeedCommentService,
  addGameFeedReplyService,
  deleteGameFeedCommentService,
  getGameFeedCommentsService,
  getGameFeedRepliesService,
} from "../../services/games/gameFeed/gameFeed.service";

const COMMENTS_PAGE_SIZE = 20;

export const useGetGameFeedComments = (provider, gameId, enabled = false) => {
  const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch} = useInfiniteQuery({
    queryKey: ["gameFeedComments", provider, gameId],
    queryFn: ({pageParam = 1}) =>
      getGameFeedCommentsService({provider, gameId, page: pageParam, pageSize: COMMENTS_PAGE_SIZE}),
    getNextPageParam: (lastPage, allPages) => {
      const commentData = lastPage?.data?.data;
      if (!commentData) return undefined;
      const totalPages = Math.ceil(commentData.total / commentData.page_size);
      const nextPage = allPages.length + 1;
      return nextPage <= totalPages ? nextPage : undefined;
    },
    enabled,
    staleTime: 30 * 1000,
  });

  const comments = data?.pages?.flatMap(page => page?.data?.data?.comments ?? []) ?? [];
  const total = data?.pages?.[0]?.data?.data?.total ?? 0;

  return {comments, total, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch};
};

export const useAddGameFeedComment = () => {
  const queryClient = useQueryClient();
  const [moderationReason, setModerationReason] = useState(null);

  const {mutate, isPending} = useMutation({
    mutationFn: addGameFeedCommentService,
    onSuccess: (_, variables) => {
      setModerationReason(null);
      queryClient.invalidateQueries({queryKey: ["gameFeedComments", variables.gameProvider, variables.gameId]});
    },
    onError: error => {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 403 && message === "comment_moderated") {
        const reason = error?.response?.data?.moderation_reason || "blocklist";
        setModerationReason(reason);
      }
    },
  });

  const clearModerationError = () => setModerationReason(null);

  return {addComment: mutate, isAdding: isPending, moderationReason, clearModerationError};
};

export const useDeleteGameFeedComment = () => {
  const queryClient = useQueryClient();

  const {mutate, isPending} = useMutation({
    mutationFn: deleteGameFeedCommentService,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["gameFeedComments"]});
    },
  });

  return {deleteComment: mutate, isDeleting: isPending};
};

const REPLIES_PAGE_SIZE = 20;

export const useGetGameFeedReplies = (commentId, enabled = false) => {
  const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch} = useInfiniteQuery({
    queryKey: ["gameFeedReplies", commentId],
    queryFn: ({pageParam = 1}) => getGameFeedRepliesService({commentId, page: pageParam, pageSize: REPLIES_PAGE_SIZE}),
    getNextPageParam: (lastPage, allPages) => {
      const replyData = lastPage?.data?.data;
      if (!replyData) return undefined;
      const totalPages = Math.ceil(replyData.total / replyData.page_size);
      const nextPage = allPages.length + 1;
      return nextPage <= totalPages ? nextPage : undefined;
    },
    enabled,
    staleTime: 30 * 1000,
  });

  const replies = data?.pages?.flatMap(page => page?.data?.data?.replies ?? []) ?? [];
  const total = data?.pages?.[0]?.data?.data?.total ?? 0;

  return {replies, total, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch};
};

export const useAddGameFeedReply = () => {
  const queryClient = useQueryClient();
  const [moderationReason, setModerationReason] = useState(null);

  const {mutate, isPending} = useMutation({
    mutationFn: addGameFeedReplyService,
    onSuccess: (_, variables) => {
      setModerationReason(null);
      queryClient.invalidateQueries({queryKey: ["gameFeedReplies", variables.commentId]});
      // Also invalidate parent comments to update reply_count.
      queryClient.invalidateQueries({queryKey: ["gameFeedComments"]});
    },
    onError: error => {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 403 && message === "comment_moderated") {
        const reason = error?.response?.data?.moderation_reason || "blocklist";
        setModerationReason(reason);
      }
    },
  });

  const clearModerationError = () => setModerationReason(null);

  return {
    addReply: mutate,
    isAddingReply: isPending,
    replyModerationReason: moderationReason,
    clearReplyModerationError: clearModerationError,
  };
};
